<script setup>
import { computed, ref } from 'vue'
import { Head } from '@inertiajs/vue3'
import { Plane, ListChecks, Check, Circle, User, CalendarClock, MessageCircle } from 'lucide-vue-next'
import PageHeader from '../Components/PageHeader.vue'
import Card from '../Components/Card.vue'
import CtaButton from '../Components/CtaButton.vue'
import AircraftDiagram from '../Components/AircraftDiagram.vue'
import { status, STATUS } from '../lib/status'

const props = defineProps({
  aircraft: { type: Object, required: true },
  serviceOrder: { type: String, required: true },
  components: { type: Array, required: true },
})

const selectedId = ref(props.components[0]?.id ?? null)
const selected = computed(() => props.components.find((c) => c.id === selectedId.value))

const legend = ['done', 'in_progress', 'pending']

const countFor = (state) => props.components.filter((c) => c.state === state).length
</script>

<template>
  <Head title="Mapa da Aeronave" />

  <div class="space-y-6">
    <PageHeader title="MAPA DA AERONAVE" :aircraft="aircraft" />

    <div class="grid grid-cols-1 gap-6 xl:grid-cols-3">
      <div class="space-y-6 xl:col-span-2">
        <Card title="Componentes monitorados" :icon="Plane">
          <p class="text-sm text-slate-500">
            Selecione um ponto para ver o andamento do componente · {{ serviceOrder }}
          </p>

          <div class="mx-auto mt-4 w-full max-w-2xl">
            <AircraftDiagram
              :components="components"
              :selected-id="selectedId"
              @select="selectedId = $event"
            />
          </div>

          <ul class="mt-4 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 border-t border-slate-100 pt-4">
            <li v-for="state in legend" :key="state" class="flex items-center gap-2 text-xs text-slate-600">
              <span class="h-2.5 w-2.5 rounded-full" :class="STATUS[state].dot" aria-hidden="true" />
              {{ STATUS[state].label }} ({{ countFor(state) }})
            </li>
          </ul>
        </Card>

        <Card title="Todos os componentes" :icon="ListChecks">
          <ul class="divide-y divide-slate-100 overflow-hidden rounded-lg border border-slate-100">
            <li v-for="item in components" :key="item.id">
              <button
                type="button"
                class="flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors"
                :class="selectedId === item.id ? 'bg-up-bg' : 'hover:bg-up-bg'"
                :aria-pressed="selectedId === item.id"
                @click="selectedId = item.id"
              >
                <span class="h-3 w-3 shrink-0 rounded-full" :class="status(item.state).dot" aria-hidden="true" />
                <span class="font-medium text-up-navy">{{ item.part }}</span>
                <span class="ml-auto shrink-0 text-xs" :class="status(item.state).text">
                  {{ status(item.state).label }}
                </span>
              </button>
            </li>
          </ul>
        </Card>
      </div>

      <div class="space-y-6">
        <Card v-if="selected" :title="selected.part" :icon="Plane">
          <span
            class="inline-block self-start rounded-md px-2.5 py-1 text-xs font-medium"
            :class="status(selected.state).badge"
          >{{ status(selected.state).label }}</span>

          <p class="mt-3 text-sm leading-relaxed text-slate-600">{{ selected.summary }}</p>

          <ul class="mt-4 space-y-2">
            <li v-for="task in selected.tasks" :key="task.label" class="flex items-center gap-2.5">
              <span
                class="flex h-5 w-5 shrink-0 items-center justify-center rounded-full"
                :class="status(task.state).icon"
                aria-hidden="true"
              >
                <Check v-if="task.state === 'done'" class="h-3 w-3" stroke-width="3" />
                <Circle v-else class="h-2 w-2 fill-current" />
              </span>
              <span class="text-sm text-up-navy">{{ task.label }}</span>
              <span class="sr-only">{{ status(task.state).label }}</span>
            </li>
          </ul>

          <dl class="mt-5 space-y-2 border-t border-slate-100 pt-4 text-xs text-slate-500">
            <div class="flex items-center gap-2">
              <User class="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <dt class="sr-only">Responsável</dt>
              <dd>{{ selected.technician }}</dd>
            </div>
            <div class="flex items-center gap-2">
              <CalendarClock class="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <dt class="sr-only">Última atualização</dt>
              <dd>Atualizado em {{ selected.updated_at }}</dd>
            </div>
          </dl>
        </Card>

        <Card>
          <p class="text-sm leading-relaxed text-slate-600">
            Quer detalhes de alguma intervenção? A equipe técnica explica ponto a ponto.
          </p>
          <div class="pt-4">
            <CtaButton href="/suporte?assunto=manutencao" variant="outline" :icon="MessageCircle">
              Falar com a equipe
            </CtaButton>
          </div>
        </Card>
      </div>
    </div>
  </div>
</template>
